import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import SessionResumeDialog from './components/SessionResumeDialog';
import BlueOxAuthBridge from './components/BlueOxAuthBridge';
import { AttachmentViewerProvider } from './contexts/AttachmentViewerContext';
import { AddTodoFromRecordProvider } from './contexts/AddTodoFromRecordContext';

import ProtectedRoute from './components/ProtectedRoute';
import ResizableModals from './components/ResizableModals';
import Layout from './components/Layout';
import ErrorBoundary from './components/ErrorBoundary';
import LoginPage from './pages/misc/LoginPage';
import ForgotPasswordPage from './pages/misc/ForgotPasswordPage';
import ResetPasswordPage from './pages/misc/ResetPasswordPage';
import { ThemeProvider as CustomThemeProvider } from './contexts/ThemeContext';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { GlobalStyles, Box, CircularProgress } from '@mui/material';
import './App.css';
import '@blueox/components/styles.css';

// Login stays in the first download. Every other page loads when its route opens.
const Dashboard = lazy(() => import('./pages/reports/Dashboard'));
const CalendarPage = lazy(() => import('./pages/calendar/CalendarPage'));
const ComplianceMap = lazy(() => import('./pages/reports/ComplianceMap'));
const CompliancePage = lazy(() => import('./pages/compliance-records/CompliancePage'));
const ReportsPage = lazy(() => import('./pages/misc/ReportsPage'));
const DocumentsPage = lazy(() => import('./pages/misc/DocumentsPage'));
const SharePointDocumentsPage = lazy(() => import('./pages/sharepoint/SharePointDocumentsPage'));
const SharePointAdminResourcesPage = lazy(() => import('./pages/administration/SharePointAdminResourcesPage'));
const UserManagementPage = lazy(() => import('./pages/administration/UserManagementPage'));
const UserAccessPage = lazy(() => import('./pages/administration/UserAccessPage'));
const UserAccessMatrixPage = lazy(() => import('./pages/administration/UserAccessMatrixPage'));
const GoverningBodiesManagementPage = lazy(() => import('./pages/audit-management/GoverningBodiesManagementPage'));
const AddEnginePage = lazy(() => import('./pages/engines/AddEnginePage'));
const AddEngineUsePage = lazy(() => import('./pages/engines/AddEngineUsePage'));
const EngineRunningHoursPage = lazy(() => import('./pages/engines/EngineRunningHoursPage'));
const SettingsPage = lazy(() => import('./pages/misc/SettingsPage'));
const BugReportsPage = lazy(() => import('./pages/misc/BugReportsPage'));
const HomePage = lazy(() => import('./pages/home/HomePage'));
const TodoListPage = lazy(() => import('./pages/todo-list/TodoListPage'));
const SolidWasteCompliancePage = lazy(() => import('./pages/misc/SolidWasteCompliancePage'));
const ComplianceSummaryReportPage = lazy(() => import('./pages/reports/ComplianceSummaryReportPage'));
const ComplianceVerificationsPage = lazy(() => import('./pages/audit-management/ComplianceVerificationsPage'));
const DrainageRecordationPage = lazy(() => import('./pages/inspections/DrainageRecordationPage'));
const SPCCRoutineInspectionPage = lazy(() => import('./pages/inspections/SPCCRoutineInspectionPage'));
const SPCCAnnualInspectionPage = lazy(() => import('./pages/inspections/SPCCAnnualInspectionPage'));
const SWPPPDischargeMonitoringInspectionPage = lazy(() => import('./pages/inspections/SWPPPDischargeMonitoringInspectionPage'));
const SWPPPQuarterlyInspectionPage = lazy(() => import('./pages/inspections/SWPPPQuarterlyInspectionPage'));
const QINotificationExerciseLogPage = lazy(() => import('./pages/testing/QINotificationExerciseLogPage'));
const AnnualTabletopExerciseLogPage = lazy(() => import('./pages/testing/AnnualTabletopExerciseLogPage'));
const EquipmentDeploymentPage = lazy(() => import('./pages/testing/EquipmentDeploymentPage'));
const TransferHoseAndPipeTestingPage = lazy(() => import('./pages/testing/TransferHoseAndPipeTestingPage'));
const EnvironmentalSamplingPage = lazy(() => import('./pages/testing/EnvironmentalSamplingPage'));
const EnvironmentalLabAnalysisPage = lazy(() => import('./pages/testing/EnvironmentalLabAnalysisPage'));
const HazardousWasteRemovalPage = lazy(() => import('./pages/maintenance/HazardousWasteRemovalPage'));
const GlycolPumpsPage = lazy(() => import('./pages/air-permitting/GlycolPumpsPage'));
const FlaresPage = lazy(() => import('./pages/air-permitting/FlaresPage'));
const GlycolPumpLogPage = lazy(() => import('./pages/air-permitting/GlycolPumpLogPage'));
const OpacityMonitoringPage = lazy(() => import('./pages/air-permitting/OpacityMonitoringPage'));
const FlareLogPage = lazy(() => import('./pages/air-permitting/FlareLogPage'));
const SumpPumpMaintenanceInspectionPage = lazy(() => import('./pages/maintenance/SumpPumpMaintenanceInspectionPage'));
const ResponseEquipmentInventoryInspectionPage = lazy(() => import('./pages/inspections/ResponseEquipmentInventoryInspectionPage'));
const LibraryOfRecordsPage = lazy(() => import('./pages/library-of-records/LibraryOfRecordsPage'));
const AuthorizedWasteReceiptPage = lazy(() => import('./pages/r9-facility/AuthorizedWasteReceiptPage'));
const AnalyticalResultFormForAWRPage = lazy(() => import('./pages/r9-facility/AnalyticalResultFormForAWRPage'));
const OutgoingSolidWastePage = lazy(() => import('./pages/r9-facility/OutgoingSolidWastePage'));
const OutgoingProducedWaterWastePage = lazy(() => import('./pages/r9-facility/OutgoingProducedWaterWastePage'));
const AWRApproversPage = lazy(() => import('./pages/r9-administration/AWRApproversPage'));
const InvoicingPage = lazy(() => import('./pages/r9-administration/InvoicingPage'));
const WasteGeneratorFacilitiesPage = lazy(() => import('./pages/r9-administration/WasteGeneratorFacilitiesPage'));
const BillingCompaniesPage = lazy(() => import('./pages/r9-administration/BillingCompaniesPage'));
const WasteTransportersPage = lazy(() => import('./pages/r9-administration/WasteTransportersPage'));
const DisposalFacilitiesPage = lazy(() => import('./pages/r9-administration/DisposalFacilitiesPage'));
const R9QuarterlyReportPage = lazy(() => import('./pages/r9-administration/R9QuarterlyReportPage'));
const WeeklyEquipmentServiceLogsPage = lazy(() => import('./pages/pws-compliance/WeeklyEquipmentServiceLogsPage'));
const WeeklyPWSWaterMonitoringPage = lazy(() => import('./pages/pws-compliance/WeeklyPWSWaterMonitoringPage'));
const PWSReportPage = lazy(() => import('./pages/reports/PWSReportPage'));
const MonthlyPWSWaterSamplePage = lazy(() => import('./pages/pws-compliance/MonthlyPWSWaterSamplePage'));
const MonthlyPWSAnalyticalPage = lazy(() => import('./pages/pws-compliance/MonthlyPWSAnalyticalPage'));
const QuarterlyDLQORPage = lazy(() => import('./pages/pws-compliance/QuarterlyDLQORPage'));
const PwsLogRecordAssignmentPage = lazy(() => import('./pages/administration/PwsLogRecordAssignmentPage'));
const WeeklyReclaimedWaterMonitoringPage = lazy(() => import('./pages/reclaimed-water-compliance/WeeklyReclaimedWaterMonitoringPage'));
const MonthlyReclaimedWaterSamplePage = lazy(() => import('./pages/reclaimed-water-compliance/MonthlyReclaimedWaterSamplePage'));
const MonthlyReclaimedAnalyticalReportReviewPage = lazy(() => import('./pages/reclaimed-water-compliance/MonthlyReclaimedAnalyticalReportReviewPage'));
const SemiAnnualReclaimedWaterSamplePage = lazy(() => import('./pages/reclaimed-water-compliance/SemiAnnualReclaimedWaterSamplePage'));
const SemiAnnualReclaimedAnalyticalReportReviewPage = lazy(() => import('./pages/reclaimed-water-compliance/SemiAnnualReclaimedAnalyticalReportReviewPage'));
const AnnualReclaimedWaterSamplePage = lazy(() => import('./pages/reclaimed-water-compliance/AnnualReclaimedWaterSamplePage'));
const AnnualReclaimedAnalyticalReportReviewPage = lazy(() => import('./pages/reclaimed-water-compliance/AnnualReclaimedAnalyticalReportReviewPage'));
const AnnualAnalyticalDataSummaryPage = lazy(() => import('./pages/reclaimed-water-compliance/AnnualAnalyticalDataSummaryPage'));
const MonthlyReclaimedAnalyticalDataSummaryPage = lazy(() => import('./pages/reclaimed-water-compliance/MonthlyReclaimedAnalyticalDataSummaryPage'));
const SiteManagementPage = lazy(() => import('./pages/administration/SiteManagementPage'));
const FormStatusManagementPage = lazy(() => import('./pages/administration/FormStatusManagementPage'));
const TemplatesManagementPage = lazy(() => import('./pages/administration/TemplatesManagementPage'));
const CompanyManagementPage = lazy(() => import('./pages/administration/CompanyManagementPage'));
const ManageComplianceElementsPage = lazy(() => import('./pages/administration/ManageComplianceElementsPage'));
const ManageComplianceAspectsPage = lazy(() => import('./pages/administration/ManageComplianceAspectsPage'));
const ManageRecordTypesPage = lazy(() => import('./pages/administration/ManageRecordTypesPage'));
const ManageProjectTypesPage = lazy(() => import('./pages/administration/ManageProjectTypesPage'));
const ManagePermitAgenciesPage = lazy(() => import('./pages/administration/ManagePermitAgenciesPage'));
const ManageProjectPermitTypesPage = lazy(() => import('./pages/administration/ManageProjectPermitTypesPage'));
const ManageProjectElementsPage = lazy(() => import('./pages/administration/ManageProjectElementsPage'));
const ManageApprovalParametersPage = lazy(() => import('./pages/administration/ManageApprovalParametersPage'));
const HazardousWasteDisposalSiteManagementPage = lazy(() => import('./pages/administration/HazardousWasteDisposalSiteManagementPage'));
const WasteUnitsAndConversionsPage = lazy(() => import('./pages/administration/WasteUnitsAndConversionsPage'));
const OfflineSyncPage = lazy(() => import('./pages/offline-sync/OfflineSyncPage'));
const UserProfilePage = lazy(() => import('./pages/misc/UserProfilePage'));
const ProjectListPage = lazy(() => import('./pages/project-permitting/ProjectListPage'));
const ProjectWorkspacePage = lazy(() => import('./pages/project-permitting/ProjectWorkspacePage'));
const ProjectFilesPage = lazy(() => import('./pages/project-permitting/ProjectFilesPage'));
const ComplianceOrderManagementPage = lazy(() => import('./pages/compliance-order-management/ComplianceOrderManagementPage'));
const ConocoTasksPage = lazy(() => import('./pages/conoco-tasks/ConocoTasksPage'));
const ConocoTaskGroupsPage = lazy(() => import('./pages/conoco-tasks/ConocoTaskGroupsPage'));
const ConocoTaskGroupDetailPage = lazy(() => import('./pages/conoco-tasks/ConocoTaskGroupDetailPage'));
const ComplianceOrderVerificationsPage = lazy(() => import('./pages/compliance-order-management/ComplianceOrderVerificationsPage'));
const ComplianceOrderActionCodesPage = lazy(() => import('./pages/compliance-order-management/ComplianceOrderActionCodesPage'));
const ComplianceOrderWorkTypesPage = lazy(() => import('./pages/compliance-order-management/ComplianceOrderWorkTypesPage'));
const FieldsManagementPage = lazy(() => import('./pages/engines/FieldsManagementPage'));
const WellsManagementPage = lazy(() => import('./pages/engines/WellsManagementPage'));
const ComplianceOrderDetailPage = lazy(() => import('./pages/compliance-order-management/ComplianceOrderDetailPage'));
const ComplianceOrdersReportPage = lazy(() => import('./pages/reports/ComplianceOrdersReportPage'));
const PopoutPage = lazy(() => import('./pages/misc/PopoutPage'));
const ComplianceVerificationDetailPage = lazy(() => import('./pages/audit-management/ComplianceVerificationDetailPage'));
const AddComplianceVerificationMasterPage = lazy(() => import('./pages/audit-management/AddComplianceVerificationMasterPage'));
const AddComplianceVerificationRecordPage = lazy(() => import('./pages/audit-management/AddComplianceVerificationRecordPage'));
const QuestionBankManagementPage = lazy(() => import('./pages/audit-management/QuestionBankManagementPage'));
const AuditScopeManagementPage = lazy(() => import('./pages/audit-management/AuditScopeManagementPage'));
const QuestionsPage = lazy(() => import('./pages/audit-management/QuestionsPage'));
const VerificationQuestionnairePage = lazy(() => import('./pages/audit-management/VerificationQuestionnairePage'));
const VerificationDashboardPage = lazy(() => import('./pages/reports/VerificationDashboardPage'));
const StandardsAndRegulationsManagementPage = lazy(() => import('./pages/audit-management/StandardsAndRegulationsManagementPage'));
const SchedulerMonitorPage = lazy(() => import('./pages/administration/SchedulerMonitorPage'));
const QaLabPage = lazy(() => import('./pages/administration/QaLabPage'));
const CustomFormBuilderPage = lazy(() => import('./pages/administration/CustomFormBuilderPage'));
const CustomFormBuilderStudioPage = lazy(() => import('./pages/administration/CustomFormBuilderStudioPage'));
const CustomFormEntryPage = lazy(() => import('./pages/administration/CustomFormEntryPage'));
const CustomReportBuilderPage = lazy(() => import('./pages/administration/CustomReportBuilderPage'));
const CustomReportBuilderStudioPage = lazy(() => import('./pages/administration/CustomReportBuilderStudioPage'));
const CustomReportViewerPage = lazy(() => import('./pages/administration/CustomReportViewerPage'));
const EmailTestPage = lazy(() => import('./pages/administration/EmailTestPage'));
const PdfTestingPage = lazy(() => import('./pages/administration/PdfTestingPage'));
const WeeklyFlowMeterLogsPage = lazy(() => import('./pages/pws-compliance/WeeklyFlowMeterLogsPage'));
const QuarterlyColorimeterCalibrationVerificationPage = lazy(() => import('./pages/pws-compliance/QuarterlyColorimeterCalibrationVerificationPage'));

const LazyPageFallback = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
    <CircularProgress />
  </Box>
);

function AppPage({ children }) {
  const location = useLocation();
  return (
    <ProtectedRoute>
      <Layout>
        <ErrorBoundary
          resetKey={location.pathname}
          context="Page"
          message="This page ran into a problem. Retry to load it again."
        >
          <Suspense fallback={<LazyPageFallback />}>{children}</Suspense>
        </ErrorBoundary>
      </Layout>
    </ProtectedRoute>
  );
}

// Create a no-transition component for tooltips.
// Must forward ref to a DOM node — Popper uses it for placement (fragment → top-left).
const NoTransition = React.forwardRef(function NoTransition(props, ref) {
  const {
    children,
    in: _in,
    appear: _appear,
    enter: _enter,
    exit: _exit,
    onEnter: _onEnter,
    onEntering: _onEntering,
    onEntered: _onEntered,
    onExit: _onExit,
    onExiting: _onExiting,
    onExited: _onExited,
    addEndListener: _addEndListener,
    ...other
  } = props;
  return (
    <span ref={ref} {...other}>
      {children}
    </span>
  );
});

const DIALOG_MAX_WIDTH = {
  xs: '360px',
  sm: '600px',
  md: '800px',
  lg: '960px',
  xl: '1200px',
};

const containedMaxWidth = {
  maxWidth: '100%',
  minWidth: 0,
};

const muiTheme = createTheme({
  zIndex: {
    snackbar: 10050,
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: containedMaxWidth,
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: containedMaxWidth,
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: { maxWidth: '100%' },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: { maxWidth: '100%' },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: { maxWidth: '100%' },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: ({ ownerState }) => {
          if (ownerState.fullWidth || ownerState.fullScreen || !ownerState.maxWidth) {
            return {};
          }
          const maxWidth = DIALOG_MAX_WIDTH[ownerState.maxWidth] || DIALOG_MAX_WIDTH.sm;
          return { '--dialog-max-width': maxWidth };
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--color-border-input)',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--color-border-input)',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--color-border-input)',
            borderWidth: 1.5,
          },
          '&.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--color-border-input)',
          },
          '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--color-border-input)',
          },
        },
      },
    },
    MuiSnackbar: {
      defaultProps: {
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
      },
      styleOverrides: {
        root: ({ theme }) => ({
          top: 24,
          bottom: 'auto',
          zIndex: theme.zIndex.snackbar,
          position: 'fixed',
        }),
      },
    },
    MuiTooltip: {
      defaultProps: {
        TransitionComponent: NoTransition,
        disableHoverListener: false,
        disableFocusListener: false,
        disableTouchListener: false,
      },
      styleOverrides: {
        tooltip: {
          backgroundColor: 'var(--color-bg-alt)',
          color: 'var(--color-text)',
          border: '1px solid var(--color-border)',
          fontSize: '12px',
          maxWidth: '300px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          // Disable animations but preserve positioning
          transition: 'none !important',
          animation: 'none !important',
          // Don't disable transform - it's needed for positioning
        },
        popper: {
          // Disable animations but preserve positioning
          transition: 'none !important',
          animation: 'none !important',
          // Don't disable transform - it's needed for positioning
        },
      },
    },
  },
});

function App() {
  return (
    <ThemeProvider theme={muiTheme}>
      <GlobalStyles styles={{
        '.custom-datepicker-input .MuiOutlinedInput-root': {
          border: '1px solid var(--color-border-input) !important',
          borderRadius: '8px',
        },
        '.custom-datepicker-input .MuiOutlinedInput-root.Mui-focused': {
          border: '1.5px solid var(--color-accent) !important',
        },
        '.custom-datepicker-input .MuiOutlinedInput-root.Mui-error': {
          border: '1px solid var(--color-border-input) !important',
        },
        '.custom-datepicker-input .MuiOutlinedInput-root.Mui-disabled': {
          border: '1px solid var(--color-border-input) !important',
        },
        '.custom-datepicker-input .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
          borderColor: 'var(--color-border-input) !important',
        },
        '.custom-datepicker-input .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: 'var(--color-accent) !important',
          borderWidth: '1.5px !important',
        },
        '.custom-datepicker-input .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
          borderColor: 'var(--color-border-input) !important',
        },
        '.custom-datepicker-input .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline': {
          borderColor: 'var(--color-border-input) !important',
        },
      }} />
      <CustomThemeProvider>
        <AuthProvider>
          <SessionResumeDialog />
          <BlueOxAuthBridge>
          <Router>
            <AttachmentViewerProvider>
            <AddTodoFromRecordProvider>
            <div className="App">
              <Routes>
                <Route path="/login" element={<LoginPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route
                  path="/popout/:modalKey"
                  element={
                    <ProtectedRoute>
                      <Suspense fallback={<LazyPageFallback />}>
                        <PopoutPage />
                      </Suspense>
                    </ProtectedRoute>
                  }
                />
                <Route path="/" element={
                  <AppPage>
                      <HomePage />
                    </AppPage>
                } />
                <Route path="/todo-list" element={
                  <AppPage>
                      <TodoListPage />
                    </AppPage>
                } />
                <Route
                  path="/dashboard"
                  element={
                    <AppPage>
                        <Dashboard />
                      </AppPage>
                  }
                />
                <Route
                  path="/compliance-orders-report"
                  element={
                    <AppPage>
                        <ComplianceOrdersReportPage />
                      </AppPage>
                  }
                />
                <Route
                  path="/compliance"
                  element={
                    <AppPage>
                        <CompliancePage />
                      </AppPage>
                  }
                />
                <Route
                  path="/reports"
                  element={
                    <AppPage>
                        <ReportsPage />
                      </AppPage>
                  }
                />
                <Route
                  path="/documents"
                  element={
                    <AppPage>
                        <DocumentsPage />
                      </AppPage>
                  }
                />
                <Route
                  path="/users/matrix"
                  element={
                    <AppPage>
                        <UserAccessMatrixPage />
                      </AppPage>
                  }
                />
                <Route
                  path="/users/:userId/access"
                  element={
                    <AppPage>
                        <UserAccessPage />
                      </AppPage>
                  }
                />
                <Route
                  path="/users"
                  element={
                    <AppPage>
                        <UserManagementPage />
                      </AppPage>
                  }
                />
                <Route
                  path="/governing-bodies"
                  element={
                    <AppPage>
                        <GoverningBodiesManagementPage />
                      </AppPage>
                  }
                />
                <Route
                  path="/engines"
                  element={
                    <AppPage>
                        <AddEnginePage />
                      </AppPage>
                  }
                />
                <Route path="/engines/add" element={<Navigate to="/engines" replace />} />
                <Route
                  path="/engines/engine-uses/add"
                  element={
                    <AppPage>
                        <AddEngineUsePage />
                      </AppPage>
                  }
                />
                <Route
                  path="/engine-running-hours"
                  element={
                    <AppPage>
                        <EngineRunningHoursPage />
                      </AppPage>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <AppPage>
                        <SettingsPage />
                      </AppPage>
                  }
                />
                <Route
                  path="/bug-reports"
                  element={
                    <AppPage>
                        <BugReportsPage />
                      </AppPage>
                  }
                />
                <Route
                  path="/bug-reports/:id"
                  element={
                    <AppPage>
                        <BugReportsPage />
                      </AppPage>
                  }
                />
                <Route
                  path="/profile"
                  element={
                    <AppPage>
                        <UserProfilePage />
                      </AppPage>
                  }
                />
                <Route
                  path="/calendar"
                  element={
                    <AppPage>
                        <CalendarPage />
                      </AppPage>
                  }
                />
                <Route
                  path="/compliance-map"
                  element={
                    <AppPage>
                        <ComplianceMap />
                      </AppPage>
                  }
                />
                <Route
                  path="/solid-waste-compliance"
                  element={
                    <AppPage>
                        <SolidWasteCompliancePage />
                      </AppPage>
                  }
                />
                
                {/* New Routes */}
                <Route path="/compliance-summary-report" element={<AppPage><ComplianceSummaryReportPage /></AppPage>} />
                <Route path="/audits" element={<AppPage><ComplianceVerificationsPage /></AppPage>} />
                <Route path="/audit-scopes" element={<AppPage><AuditScopeManagementPage /></AppPage>} />
                <Route path="/audit-scopes/:scopeId/questions" element={<AppPage><QuestionsPage /></AppPage>} />
                <Route path="/question-banks" element={<AppPage><QuestionBankManagementPage /></AppPage>} />
                <Route path="/question-banks/:bankId/questions" element={<AppPage><QuestionsPage /></AppPage>} />
                <Route path="/standards-and-regulations" element={<AppPage><StandardsAndRegulationsManagementPage /></AppPage>} />
                <Route path="/pre-audit-questionnaires" element={<AppPage><VerificationQuestionnairePage /></AppPage>} />
                <Route path="/audits-report" element={<AppPage><VerificationDashboardPage /></AppPage>} />
                <Route path="/verification-dashboard" element={<Navigate to="/audits-report" replace />} />
                <Route path="/compliance-order-management" element={<Navigate to="/compliance-orders" replace />} />
                <Route path="/drainage-recordation" element={<AppPage><DrainageRecordationPage /></AppPage>} />
                <Route path="/spcc-routine-inspection" element={<AppPage><SPCCRoutineInspectionPage /></AppPage>} />
                <Route path="/spcc-annual-inspection" element={<AppPage><SPCCAnnualInspectionPage /></AppPage>} />
                <Route path="/swppp-discharge-monitoring-inspection" element={<AppPage><SWPPPDischargeMonitoringInspectionPage /></AppPage>} />
                <Route path="/swppp-quarterly-inspection" element={<AppPage><SWPPPQuarterlyInspectionPage /></AppPage>} />
                <Route path="/qi-notification-exercise-log" element={<AppPage><QINotificationExerciseLogPage /></AppPage>} />
                <Route path="/annual-tabletop-exercise-log" element={<AppPage><AnnualTabletopExerciseLogPage /></AppPage>} />
                <Route path="/equipment-deployment-exercise-log" element={<AppPage><EquipmentDeploymentPage /></AppPage>} />
                <Route path="/transfer-hose-and-pipe-testing" element={<AppPage><TransferHoseAndPipeTestingPage /></AppPage>} />
                <Route path="/environmental-sampling" element={<AppPage><EnvironmentalSamplingPage /></AppPage>} />
                <Route path="/environmental-lab-analysis" element={<AppPage><EnvironmentalLabAnalysisPage /></AppPage>} />
                <Route path="/hazardous-waste-removal" element={<AppPage><HazardousWasteRemovalPage /></AppPage>} />
                <Route path="/air-glycol-pumps" element={<AppPage><GlycolPumpsPage /></AppPage>} />
                <Route path="/air-flares" element={<AppPage><FlaresPage /></AppPage>} />
                <Route path="/air-glycol-pump-log" element={<AppPage><GlycolPumpLogPage /></AppPage>} />
                <Route path="/air-opacity-monitoring" element={<AppPage><OpacityMonitoringPage /></AppPage>} />
                <Route path="/air-flare-log" element={<AppPage><FlareLogPage /></AppPage>} />
                <Route path="/sump-pump-maintenance-inspection" element={<AppPage><SumpPumpMaintenanceInspectionPage /></AppPage>} />
                <Route path="/response-equipment-inventory-inspection" element={<AppPage><ResponseEquipmentInventoryInspectionPage /></AppPage>} />
                <Route path="/library-of-records" element={<AppPage><LibraryOfRecordsPage /></AppPage>} />
                <Route path="/sharepoint-documents" element={<AppPage><SharePointDocumentsPage /></AppPage>} />
                <Route path="/sharepoint-admin-resources" element={<AppPage><SharePointAdminResourcesPage /></AppPage>} />
                <Route path="/authorized-waste-receipt" element={<AppPage><AuthorizedWasteReceiptPage /></AppPage>} />
                <Route path="/analytical-result-form-for-awr" element={<AppPage><AnalyticalResultFormForAWRPage /></AppPage>} />
                <Route path="/outgoing-solid-waste" element={<AppPage><OutgoingSolidWastePage /></AppPage>} />
                <Route path="/outgoing-produced-water-waste" element={<AppPage><OutgoingProducedWaterWastePage /></AppPage>} />
                <Route path="/awr-approvers" element={<AppPage><AWRApproversPage /></AppPage>} />
                <Route path="/invoicing" element={<AppPage><InvoicingPage /></AppPage>} />
                <Route path="/waste-generator-sites" element={<AppPage><WasteGeneratorFacilitiesPage /></AppPage>} />
                <Route path="/billing-companies" element={<AppPage><BillingCompaniesPage /></AppPage>} />
                <Route path="/waste-transporters" element={<AppPage><WasteTransportersPage /></AppPage>} />
                <Route path="/disposal-sites" element={<AppPage><DisposalFacilitiesPage /></AppPage>} />
                <Route path="/r9/quarterly-report" element={<AppPage><R9QuarterlyReportPage /></AppPage>} />
                <Route path="/project-permitting" element={<AppPage><ProjectListPage /></AppPage>} />
                <Route path="/project-workspace/:id" element={<AppPage><ProjectWorkspacePage /></AppPage>} />
                <Route path="/project-files" element={<AppPage><ProjectFilesPage /></AppPage>} />
                <Route path="/weekly-flow-meter-logs" element={<AppPage><WeeklyFlowMeterLogsPage /></AppPage>} />
                <Route path="/weekly-equipment-service-logs" element={<AppPage><WeeklyEquipmentServiceLogsPage /></AppPage>} />
                <Route path="/weekly-pws-water-monitoring" element={<AppPage><WeeklyPWSWaterMonitoringPage /></AppPage>} />
                <Route path="/pws-report" element={<AppPage><PWSReportPage /></AppPage>} />
                <Route path="/monthly-pws-water-sample" element={<AppPage><MonthlyPWSWaterSamplePage /></AppPage>} />
                <Route path="/monthly-pws-analytical-report-review" element={<AppPage><MonthlyPWSAnalyticalPage /></AppPage>} />
                <Route path="/quarterly-dlqor" element={<AppPage><QuarterlyDLQORPage /></AppPage>} />
                <Route path="/quarterly-colorimeter-calibration-verification" element={<AppPage><QuarterlyColorimeterCalibrationVerificationPage /></AppPage>} />
                <Route path="/weekly-reclaimed-water-monitoring" element={<AppPage><WeeklyReclaimedWaterMonitoringPage /></AppPage>} />
                <Route path="/monthly-reclaimed-water-sample" element={<AppPage><MonthlyReclaimedWaterSamplePage /></AppPage>} />
                <Route path="/monthly-reclaimed-analytical-report-review" element={<AppPage><MonthlyReclaimedAnalyticalReportReviewPage /></AppPage>} />
                <Route path="/semi-annual-reclaimed-water-sample" element={<AppPage><SemiAnnualReclaimedWaterSamplePage /></AppPage>} />
                <Route path="/semi-annual-reclaimed-analytical-report-review" element={<AppPage><SemiAnnualReclaimedAnalyticalReportReviewPage /></AppPage>} />
                <Route path="/annual-reclaimed-water-sample" element={<AppPage><AnnualReclaimedWaterSamplePage /></AppPage>} />
                <Route path="/annual-reclaimed-analytical-report-review" element={<AppPage><AnnualReclaimedAnalyticalReportReviewPage /></AppPage>} />
                <Route path="/annual-analytical-data-summary" element={<AppPage><AnnualAnalyticalDataSummaryPage /></AppPage>} />
                <Route path="/monthly-lab-summary" element={<AppPage><MonthlyReclaimedAnalyticalDataSummaryPage /></AppPage>} />
                <Route path="/site-management" element={<AppPage><SiteManagementPage /></AppPage>} />
                <Route path="/pws-log-record-assignment" element={<AppPage><PwsLogRecordAssignmentPage /></AppPage>} />
                <Route path="/form-status-management" element={<AppPage><FormStatusManagementPage /></AppPage>} />
                <Route path="/templates" element={<AppPage><TemplatesManagementPage /></AppPage>} />
                <Route path="/company-management" element={<AppPage><CompanyManagementPage /></AppPage>} />
                <Route path="/manage-compliance-elements" element={<AppPage><ManageComplianceElementsPage /></AppPage>} />
                <Route path="/manage-compliance-aspects" element={<AppPage><ManageComplianceAspectsPage /></AppPage>} />
                <Route path="/manage-record-types" element={<AppPage><ManageRecordTypesPage /></AppPage>} />
                <Route path="/manage-project-types" element={<AppPage><ManageProjectTypesPage /></AppPage>} />
                <Route path="/manage-permit-agencies" element={<AppPage><ManagePermitAgenciesPage /></AppPage>} />
                <Route path="/manage-project-permit-types" element={<AppPage><ManageProjectPermitTypesPage /></AppPage>} />
                <Route path="/manage-project-elements" element={<AppPage><ManageProjectElementsPage /></AppPage>} />
                <Route path="/manage-approval-parameters" element={<AppPage><ManageApprovalParametersPage /></AppPage>} />
                <Route path="/hazardous-waste-disposal-site-management" element={<AppPage><HazardousWasteDisposalSiteManagementPage /></AppPage>} />
                <Route path="/waste-units-and-conversions" element={<AppPage><WasteUnitsAndConversionsPage /></AppPage>} />
                <Route path="/offline-sync" element={<AppPage><OfflineSyncPage /></AppPage>} />
                
                {/* Audit (detail/master/record) Routes */}
                <Route path="/audit-detail/:masterId" element={<AppPage><ComplianceVerificationDetailPage /></AppPage>} />
                <Route path="/add-audit-master" element={<AppPage><AddComplianceVerificationMasterPage /></AppPage>} />
                <Route path="/add-audit-record/:masterId" element={<AppPage><AddComplianceVerificationRecordPage /></AppPage>} />
                
                {/* Admin Routes */}
                <Route path="/scheduler-monitor" element={<AppPage><SchedulerMonitorPage /></AppPage>} />
                <Route path="/qa-lab" element={<AppPage><QaLabPage /></AppPage>} />
                <Route path="/custom-form-builder/studio/:formId" element={<AppPage><CustomFormBuilderStudioPage /></AppPage>} />
                <Route path="/custom-form-builder" element={<AppPage><CustomFormBuilderPage /></AppPage>} />
                <Route path="/custom-report-builder/studio/:reportId" element={<AppPage><CustomReportBuilderStudioPage /></AppPage>} />
                <Route path="/custom-report-builder/view/:reportId" element={<AppPage><CustomReportViewerPage /></AppPage>} />
                <Route path="/custom-report-builder" element={<AppPage><CustomReportBuilderPage /></AppPage>} />
                <Route path="/custom-form-entry" element={<AppPage><CustomFormEntryPage /></AppPage>} />
                <Route path="/email-test" element={<AppPage><EmailTestPage /></AppPage>} />
                <Route path="/pdf-testing" element={<AppPage><PdfTestingPage /></AppPage>} />
                <Route path="/compliance-orders" element={<AppPage><ComplianceOrderManagementPage /></AppPage>} />
                <Route path="/conoco-task-groups/:groupId" element={<AppPage><ConocoTaskGroupDetailPage /></AppPage>} />
                <Route path="/conoco-task-groups" element={<AppPage><ConocoTaskGroupsPage /></AppPage>} />
                <Route path="/conoco-tasks" element={<AppPage><ConocoTasksPage /></AppPage>} />
                <Route path="/compliance-order-verifications" element={<AppPage><ComplianceOrderVerificationsPage /></AppPage>} />
                <Route path="/compliance-order-action-codes" element={<AppPage><ComplianceOrderActionCodesPage /></AppPage>} />
                <Route path="/compliance-order-work-types" element={<AppPage><ComplianceOrderWorkTypesPage /></AppPage>} />
                <Route path="/compliance-orders/:id" element={<AppPage><ComplianceOrderDetailPage /></AppPage>} />
                <Route path="/fields" element={<AppPage><FieldsManagementPage /></AppPage>} />
                <Route path="/wells" element={<AppPage><WellsManagementPage /></AppPage>} />
              </Routes>
              <ResizableModals />
            </div>
            </AddTodoFromRecordProvider>
            </AttachmentViewerProvider>
          </Router>
          </BlueOxAuthBridge>
        </AuthProvider>
      </CustomThemeProvider>
    </ThemeProvider>
  );
}

export default App;

